import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../utils';
import {
  getLastUsedBankOrCashAccount,
  GetLastUsedBankOrCashAccountQueryVariables,
  GetLastUsedBankOrCashAccountQuery,
  GetMinMaxInvoiceDateQuery,
  getMinMaxInvoiceDate,
  GetMinMaxInvoiceDateQueryVariables,
  ListPartyInvoicePurchasesQueryVariables,
  listPartyInvoicePurchases,
  ListPartyInvoicePurchasesQuery,
  getAccountBalance,
  GetAccountBalanceQueryVariables,
  GetAccountBalanceQuery,
  } from '../../../graphql';
import { RTK_CACHE_LIFETIME } from '../../../config';

type GetLastUsedBankOrCashAccountQueryResponse = {
  LastBankAccount: string | null | undefined;
  lastCashAccount: string | null | undefined;
  paymentMethodLastUsed: string | null | undefined;
};

type GetMinMaxInvoiceDateResponse = {
  prevInvoiceDate: string | null | undefined;
  nextInvoiceDate: string | null | undefined;
  latestInvoiceDate: string | null | undefined;
  openingBalanceDate: string | null | undefined;
};



export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: baseQuery,
  tagTypes: ['lastUsedAccount'],
  endpoints: (builder) => ({
    getLastUsedBankOrCashAccount: builder.query<GetLastUsedBankOrCashAccountQueryResponse, GetLastUsedBankOrCashAccountQueryVariables>({
      query: (variables) => ({ body: getLastUsedBankOrCashAccount, variables }),
      keepUnusedDataFor: RTK_CACHE_LIFETIME,
      transformResponse: (response: GetLastUsedBankOrCashAccountQuery) => {
        return {
          LastBankAccount: response.getLastUsedBankOrCashAccount?.bankpaidtoaccountid,
          lastCashAccount: response.getLastUsedBankOrCashAccount?.cashpaidtoaccountid,
          paymentMethodLastUsed: response.getLastUsedBankOrCashAccount?.paymentmethodlastused,
        };
      },
      providesTags: (result, error, arg) => [{ type: 'lastUsedAccount', id: arg.transactiontypecd }],
    }),

    getListPartyInvoicePurchases:builder.query<NonNullable<ListPartyInvoicePurchasesQuery['listPartyInvoicePurchases']>,ListPartyInvoicePurchasesQueryVariables>({
     query: (variables) => ({
        body: listPartyInvoicePurchases,
        variables,
      }),   
      transformResponse:(response: ListPartyInvoicePurchasesQuery) => {
        return response?.listPartyInvoicePurchases || [];
      },
    }),

    getMinMaxInvoiceDate: builder.query<GetMinMaxInvoiceDateResponse, GetMinMaxInvoiceDateQueryVariables>({
      query: (variables) => ({
        body: getMinMaxInvoiceDate,
        variables,
      }),
      transformResponse: (response: GetMinMaxInvoiceDateQuery) => {
        const data = response?.getMinMaxInvoiceDate;
        return {
          prevInvoiceDate: data?.previnvoicedate,
          nextInvoiceDate: data?.nextinvoicedate,
          latestInvoiceDate: data?.latestinvoicedate,
          openingBalanceDate: data?.openingbalancedate,
        };
      },
    }),

    getAccountBalance: builder.query<number | null, GetAccountBalanceQueryVariables>({
      query: (variables) => ({
        body: getAccountBalance,
        variables,
      }),
      transformResponse: (response: GetAccountBalanceQuery) => {
        if (response?.getAccountBalance && response.getAccountBalance.length > 0) {
          return response.getAccountBalance[0]?.actualbalance ?? null;
        }
        return null;
      },
    }),
  }),
});

export const {
  useLazyGetLastUsedBankOrCashAccountQuery,
  useGetLastUsedBankOrCashAccountQuery,
  useGetListPartyInvoicePurchasesQuery,
  useLazyGetListPartyInvoicePurchasesQuery,
  usePrefetch,
  useLazyGetMinMaxInvoiceDateQuery,
  useGetMinMaxInvoiceDateQuery,
  useLazyGetAccountBalanceQuery,
} = paymentApi;
